import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Input, Textarea, Select, RadioGroup, CheckboxGroup,
  RatingRow, SectionHeader, Button, Card
} from '../components/UI';
import {
  FLUENCY_OPTIONS, EDUCATION_OPTIONS, GRADUATION_YEARS, SECTOR_OPTIONS,
  AVAILABILITY_OPTIONS, DEPLOYMENT_DURATION, NOTICE_PERIOD_OPTIONS,
  FIELD_EPI_SKILLS, PROJECT_MGMT_SKILLS, ME_SKILLS, GHS_SKILLS,
  INCIDENT_SKILLS, LEADERSHIP_SKILLS, HIGH_RISK_OPTIONS, DEFAULT_FORM_VALUES,
  TITLE_OPTIONS, CONTINENTS
} from '../data/formSchema';
import styles from './AlumniForm.module.css';

const LANGUAGES = ['english', 'arabic', 'french', 'kiswahili', 'portuguese', 'chinese', 'russian', 'spanish'];
const TOTAL_STEPS = 6;

function formatDuration(seconds) {
  if (!seconds || seconds < 1) return '< 1s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}

const RATING_LABELS = ['', 'Basic', 'Limited', 'Proficient', 'Highly Proficient', 'Expert'];

export default function AlumniForm() {
  const navigate = useNavigate();

  // 'preamble' | 'form' | 'preview'
  const [stage, setStage]           = useState('preamble');
  const [step, setStep]             = useState(1);
  const [data, setData]             = useState(DEFAULT_FORM_VALUES);
  const [errors, setErrors]         = useState({});
  const [submitting, setSubmitting] = useState(false);

  const startTimeRef = useRef(null);
  const startTimerOnce = useCallback(() => {
    if (!startTimeRef.current) startTimeRef.current = Date.now();
  }, []);

  const set = (field, value) => {
    startTimerOnce();
    setData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const setNested = (parent, field, value) => {
    startTimerOnce();
    setData(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));
  };

  const setSkill = (section, key, value) => {
    startTimerOnce();
    setData(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const setRegion = (idx, value) => {
    startTimerOnce();
    setData(prev => {
      const regions = [...prev.workRegions];
      regions[idx] = value;
      return { ...prev, workRegions: regions };
    });
  };

  const toggleContinent = (continent) => {
    startTimerOnce();
    setData(prev => {
      const current = prev.workContinents || [];
      if (current.includes(continent)) {
        return { ...prev, workContinents: current.filter(c => c !== continent) };
      } else {
        return { ...prev, workContinents: [...current, continent] };
      }
    });
  };

  // Reference helpers
  const addReference = () => {
    if ((data.references || []).length >= 3) return;
    setData(prev => ({
      ...prev,
      references: [...(prev.references || []), { name: '', title: '', organization: '', email: '', phone: '' }]
    }));
  };

  const updateReference = (idx, field, value) => {
    startTimerOnce();
    setData(prev => {
      const refs = [...(prev.references || [])];
      refs[idx] = { ...refs[idx], [field]: value };
      return { ...prev, references: refs };
    });
  };

  const removeReference = (idx) => {
    setData(prev => {
      const refs = [...(prev.references || [])];
      refs.splice(idx, 1);
      return { ...prev, references: refs };
    });
  };

  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!data.title)                 e.title          = 'Required';
      if (data.title === 'Other' && !data.titleOther.trim()) e.titleOther = 'Please specify your title';
      if (!data.firstName.trim())      e.firstName      = 'Required';
      if (!data.surname.trim())        e.surname        = 'Required';
      if (!data.preferredEmail.trim()) e.preferredEmail = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.preferredEmail))
        e.preferredEmail = 'Invalid email';
      if (!data.primaryPhone.trim())   e.primaryPhone   = 'Required';
      if (!data.currentCountry.trim()) e.currentCountry = 'Required';
      if (!data.nationality.trim())    e.nationality    = 'Required';
    }
    if (step === 2) {
      if (!data.highestQualification) e.highestQualification = 'Required';
      if (!data.graduationYear)       e.graduationYear       = 'Required';
      if (!data.sector)               e.sector               = 'Required';
      if (!data.yearsExperience)      e.yearsExperience      = 'Required';
    }
    if (step === 6) {
      if (!data.consent) e.consent = 'You must consent to submit';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validateStep()) {
      if (step === TOTAL_STEPS) {
        setStage('preview');
      } else {
        setStep(s => s + 1);
      }
    }
    window.scrollTo(0, 0);
  };

  const prev = () => { setStep(s => Math.max(s - 1, 1)); window.scrollTo(0, 0); };

  const editStep = (s) => { setStep(s); setStage('form'); window.scrollTo(0, 0); };

  const submit = async () => {
    setSubmitting(true);
    const elapsedSeconds = startTimeRef.current
      ? Math.round((Date.now() - startTimeRef.current) / 1000)
      : null;
    try {
      await addDoc(collection(db, 'alumni'), {
        ...data,
        submittedAt:         serverTimestamp(),
        status:              'active',
        formDurationSeconds: elapsedSeconds,
        formDurationDisplay: formatDuration(elapsedSeconds),
      });
      navigate('/thank-you');
    } catch (err) {
      console.error(err);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step - 1) / (TOTAL_STEPS - 1)) * 100;

  /* ════════════════════════════════════════
     PREAMBLE SCREEN
  ════════════════════════════════════════ */
  if (stage === 'preamble') {
    return (
      <div className={styles.preamblePage}>
        <div className={styles.preambleCard}>
          {/* Logo */}
          <div className={styles.preambleLogo}>
            <img src="/logo.png" alt="FEwB Logo" className={styles.logoImg} />
          </div>

          {/* Title */}
          <div className={styles.preambleHeading}>
            <h1 className={styles.preambleTitle}>Alumni Questionnaire</h1>
            <div className={styles.preambleDivider} />
          </div>

          {/* Purpose */}
          <div className={styles.preambleSection}>
            <div className={styles.preambleSectionLabel}>Purpose</div>
            <p className={styles.preambleText}>
              To gather up-to-date information from FEwB alumni regarding their current professional
              status, availability for deployments, and specific skill sets, enabling FEwB to effectively
              identify and deploy appropriate expertise for public health emergencies and initiatives globally.
            </p>
          </div>

          {/* Instructions */}
          <div className={styles.preambleSection}>
            <div className={styles.preambleSectionLabel}>Instructions for Alumni</div>
            <p className={styles.preambleText}>
              Please complete this questionnaire checklist as thoroughly as possible. Your responses will
              help FEwB maintain an accurate roster of available expertise and ensure we can quickly match
              skills to needs during critical public health interventions.
            </p>
          </div>

          {/* Info chips */}
          <div className={styles.preambleChips}>
            <div className={styles.preambleChip}>
              <span className={styles.chipIcon}>📋</span>
              <span>6 Sections</span>
            </div>
            <div className={styles.preambleChip}>
              <span className={styles.chipIcon}>⏱</span>
              <span>~15–20 minutes</span>
            </div>
            <div className={styles.preambleChip}>
              <span className={styles.chipIcon}>🔒</span>
              <span>Strictly Confidential</span>
            </div>
          </div>

          {/* CTA */}
          <button
            className={styles.preambleCta}
            onClick={() => { setStage('form'); window.scrollTo(0, 0); }}
          >
            Begin Questionnaire →
          </button>

          <p className={styles.preambleNote}>
            You will be able to review all your answers before final submission.
          </p>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════
     PREVIEW SCREEN
  ════════════════════════════════════════ */
  if (stage === 'preview') {
    const displayTitle = data.title === 'Other' ? data.titleOther : data.title;
    return (
      <div className={styles.previewPage}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerInner}>
            <div className={styles.logo}>
              <img src="/logo.png" alt="FEwB" className={styles.headerLogoImg} />
            </div>
            <span className={styles.previewBadge}>Review Your Answers</span>
          </div>
        </header>

        <main className={styles.previewMain}>
          <div className={styles.previewHero}>
            <h2 className={styles.previewTitle}>Please review your responses before submitting</h2>
            <p className={styles.previewSubtitle}>
              Check every section carefully. Click <strong>Edit</strong> on any section to go back and make changes.
            </p>
          </div>

          {/* ── Section A ── */}
          <PreviewSection title="A — Personal & Contact Information" onEdit={() => editStep(1)}>
            {displayTitle && <PreviewRow label="Title"               value={displayTitle} />}
            <PreviewRow label="First Name"          value={data.firstName} />
            <PreviewRow label="Surname"              value={data.surname} />
            <PreviewRow label="Preferred Email"      value={data.preferredEmail} />
            <PreviewRow label="Alternative Email"    value={data.alternativeEmail} />
            <PreviewRow label="Primary Phone"        value={data.primaryPhone} />
            <PreviewRow label="Secondary Phone"      value={data.secondaryPhone} />
            <PreviewRow label="Country of Residence" value={data.currentCountry} />
            <PreviewRow label="Nationality"          value={data.nationality} />
            {Object.entries(data.languages).filter(([k,v]) => v && k !== 'otherLanguage').map(([lang, level]) => (
              <PreviewRow
                key={lang}
                label={lang === 'other' ? (data.languages.otherLanguage || 'Other Language') : lang.charAt(0).toUpperCase() + lang.slice(1)}
                value={level}
              />
            ))}
          </PreviewSection>

          {/* ── Section B ── */}
          <PreviewSection title="B — Professional Background" onEdit={() => editStep(2)}>
            <PreviewRow label="Highest Qualification"  value={data.highestQualification} />
            <PreviewRow label="FETP Graduation Year"   value={data.graduationYear} />
            <PreviewRow label="Current Employer"       value={data.currentEmployer} />
            <PreviewRow label="Role Description"       value={data.currentRole} />
            <PreviewRow label="Sector"                 value={data.sector} />
            {data.sectorOther && <PreviewRow label="Sector (Other)" value={data.sectorOther} />}
            <PreviewRow label="Years of Experience"    value={data.yearsExperience ? `${data.yearsExperience} years` : ''} />
          </PreviewSection>

          {/* ── Section C ── */}
          <PreviewSection title="C — Availability for Deployment" onEdit={() => editStep(3)}>
            <PreviewRow label="Local Deployment"         value={data.localDeployment} />
            <PreviewRow label="International Deployment" value={data.internationalDeployment} />
            <PreviewRow label="Notice Period"            value={data.noticePeriod} />
            {data.noticePeriodOther && <PreviewRow label="Notice (specified)" value={data.noticePeriodOther} />}
            <PreviewRow
              label="Preferred Duration"
              value={DEPLOYMENT_DURATION.filter(d => data.deploymentDuration[d.key]).map(d => d.label).join(', ')}
            />
            <PreviewRow label="Deployment Restrictions" value={data.deploymentRestrictions} />
          </PreviewSection>

          {/* ── Section D ── */}
          <PreviewSection title="D — Skills & Expertise (Self-Assessment)" onEdit={() => editStep(4)}>
            {[
              { label: '1. Field Epidemiology Core Skills', skills: FIELD_EPI_SKILLS, section: 'fieldEpiSkills', otherKey: 'fieldEpiOtherSkills' },
              { label: '2. Project Management Skills', skills: PROJECT_MGMT_SKILLS, section: 'projectMgmtSkills', otherKey: 'projectMgmtOtherSkills' },
              { label: '3. M&E Skills', skills: ME_SKILLS, section: 'meSkills', otherKey: 'meOtherSkills' },
              { label: '4. Global Health Security Skills', skills: GHS_SKILLS, section: 'ghsSkills', otherKey: 'ghsOtherSkills' },
              { label: '5. Incident Management Skills', skills: INCIDENT_SKILLS, section: 'incidentSkills', otherKey: 'incidentOtherSkills' },
              { label: '6. Leadership & Training Skills', skills: LEADERSHIP_SKILLS, section: 'leadershipSkills', otherKey: 'leadershipOtherSkills' },
            ].map(({ label, skills, section, otherKey }) => {
              const rated = skills.filter(s => (data[section][s.key] || 0) > 0);
              const hasOther = data[otherKey];
              if (rated.length === 0 && !hasOther) return null;
              return (
                <div key={section} className={styles.previewSkillGroup}>
                  <p className={styles.previewSkillGroupTitle}>{label}</p>
                  {rated.map(({ key, label: skillLabel }) => (
                    <div key={key} className={styles.previewSkillRow}>
                      <span className={styles.previewSkillLabel}>{skillLabel}</span>
                      <span className={styles.previewSkillRating}>
                        {data[section][key]} — {RATING_LABELS[data[section][key]]}
                      </span>
                    </div>
                  ))}
                  {hasOther && <PreviewRow label="Other skills" value={hasOther} />}
                </div>
              );
            })}
            {data.otherSoftwareName && <PreviewRow label="Other Software" value={data.otherSoftwareName} />}
          </PreviewSection>

          {/* ── Section E ── */}
          <PreviewSection title="E — Specific Expertise & Experience" onEdit={() => editStep(5)}>
            <PreviewRow label="Diseases/Pathogens Experience" value={data.diseasesExperience} />
            {data.workRegions.filter(Boolean).map((r, i) => (
              <PreviewRow key={i} label={`Work Region ${i + 1}`} value={r} />
            ))}
            {(data.workContinents || []).length > 0 && (
              <PreviewRow label="Continents of Work Experience" value={data.workContinents.join(', ')} />
            )}
            <PreviewRow label="Humanitarian Experience"           value={data.humanitarianExperience} />
            <PreviewRow label="Humanitarian Context"              value={data.humanitarianContext} />
            <PreviewRow label="Certifications & Training"         value={data.certifications} />
            <PreviewRow label="High-Risk Willingness"             value={data.highRiskWillingness} />
            <PreviewRow label="Additional Skills"                 value={data.additionalSkills} />
          </PreviewSection>

          {/* ── Section F ── */}
          <PreviewSection title="F — Professional References & Consent" onEdit={() => editStep(6)}>
            <PreviewRow label="Providing References" value={data.addReferences} />
            {(data.references || []).map((ref, i) => (
              <div key={i} className={styles.previewSkillGroup}>
                <p className={styles.previewSkillGroupTitle}>Reference {i + 1}</p>
                {ref.name && <PreviewRow label="Name" value={ref.name} />}
                {ref.title && <PreviewRow label="Title" value={ref.title} />}
                {ref.organization && <PreviewRow label="Organization" value={ref.organization} />}
                {ref.email && <PreviewRow label="Email" value={ref.email} />}
                {ref.phone && <PreviewRow label="Phone" value={ref.phone} />}
              </div>
            ))}
            <PreviewRow label="Data Consent" value={data.consent ? '✓ Consent given' : '✗ Not consented'} />
          </PreviewSection>

          {/* Submit bar */}
          <div className={styles.previewActions}>
            <button className={styles.previewBackBtn} onClick={() => { setStage('form'); window.scrollTo(0,0); }}>
              ← Back to Form
            </button>
            <button
              className={styles.previewSubmitBtn}
              onClick={submit}
              disabled={submitting || !data.consent}
            >
              {submitting ? 'Submitting…' : 'Confirm & Submit'}
            </button>
          </div>
          {!data.consent && (
            <p className={styles.previewConsentWarn}>
              ⚠ You must give consent in Section F before submitting.
              <button className={styles.previewConsentLink} onClick={() => editStep(6)}>Go to Section F →</button>
            </p>
          )}
        </main>
      </div>
    );
  }

  /* ════════════════════════════════════════
     FORM STEPS
  ════════════════════════════════════════ */
  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.logo}>
            <img src="/logo.png" alt="FEwB" className={styles.headerLogoImg} />
          </div>
          <span className={styles.stepLabel}>Step {step} of {TOTAL_STEPS}</span>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>
      </header>

      {/* ── Step Nav Pills ── */}
      <div className={styles.stepNav}>
        {['Personal', 'Professional', 'Availability', 'Skills', 'Experience', 'References'].map((label, i) => (
          <div
            key={i}
            className={`${styles.stepPill} ${step === i + 1 ? styles.stepPillActive : ''} ${step > i + 1 ? styles.stepPillDone : ''}`}
          >
            <span className={styles.stepNum}>{step > i + 1 ? '✓' : i + 1}</span>
            <span className={styles.stepName}>{label}</span>
          </div>
        ))}
      </div>

      <main className={styles.main}>

        {/* ── STEP 1: Personal Info ── */}
        {step === 1 && (
          <Card className={styles.card}>
            <SectionHeader letter="A" title="Personal & Contact Information" subtitle="Please provide your current personal details" />

            {/* Title row */}
            <div className={styles.grid2}>
              <div>
                <Select
                  label="Title"
                  required
                  options={TITLE_OPTIONS}
                  placeholder="Select title…"
                  value={data.title}
                  onChange={e => set('title', e.target.value)}
                  error={errors.title}
                />
                {data.title === 'Other' && (
                  <Input
                    label="Please specify title"
                    required
                    value={data.titleOther}
                    onChange={e => set('titleOther', e.target.value)}
                    error={errors.titleOther}
                    placeholder="e.g. Eng., Rev., Hon."
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
              <div />
            </div>

            <div className={styles.grid2}>
              <Input label="First Name" required value={data.firstName} onChange={e => set('firstName', e.target.value)} error={errors.firstName} placeholder="e.g. Amara" />
              <Input label="Surname" required value={data.surname} onChange={e => set('surname', e.target.value)} error={errors.surname} placeholder="e.g. Osei" />
            </div>
            <div className={styles.grid2}>
              <Input label="Preferred Email" required type="email" value={data.preferredEmail} onChange={e => set('preferredEmail', e.target.value)} error={errors.preferredEmail} placeholder="you@example.com" />
              <Input label="Alternative Email" type="email" value={data.alternativeEmail} onChange={e => set('alternativeEmail', e.target.value)} placeholder="alt@example.com" />
            </div>
            <div className={styles.grid2}>
              <Input label="Primary Phone (with country code)" required value={data.primaryPhone} onChange={e => set('primaryPhone', e.target.value)} error={errors.primaryPhone} placeholder="+254 700 000 000" />
              <Input label="Secondary Phone (with country code)" value={data.secondaryPhone} onChange={e => set('secondaryPhone', e.target.value)} placeholder="+254 711 000 000" />
            </div>
            <div className={styles.grid2}>
              <Input label="Current Country of Residence" required value={data.currentCountry} onChange={e => set('currentCountry', e.target.value)} error={errors.currentCountry} placeholder="e.g. Kenya" />
              <Input label="Nationality (Primary)" required value={data.nationality} onChange={e => set('nationality', e.target.value)} error={errors.nationality} placeholder="e.g. Kenyan" />
            </div>
            <div className={styles.subsection}>
              <p className={styles.subsectionTitle}>Languages Spoken — indicate fluency level</p>
              <div className={styles.langGrid}>
                {LANGUAGES.map(lang => (
                  <div key={lang} className={styles.langRow}>
                    <span className={styles.langName}>{lang.charAt(0).toUpperCase() + lang.slice(1)}</span>
                    <Select options={FLUENCY_OPTIONS} placeholder="Select…" value={data.languages[lang]} onChange={e => setNested('languages', lang, e.target.value)} />
                  </div>
                ))}
                <div className={styles.langRow}>
                  <Input placeholder="Other language" value={data.languages.otherLanguage} onChange={e => setNested('languages', 'otherLanguage', e.target.value)} />
                  <Select options={FLUENCY_OPTIONS} placeholder="Select…" value={data.languages.other} onChange={e => setNested('languages', 'other', e.target.value)} />
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* ── STEP 2: Professional ── */}
        {step === 2 && (
          <Card className={styles.card}>
            <SectionHeader letter="B" title="Professional Background & Current Placement" subtitle="Tell us about your education, current employer, and sector" />
            <div className={styles.grid2}>
              <Select label="Highest Educational Qualification" required options={EDUCATION_OPTIONS} placeholder="Select qualification…" value={data.highestQualification} onChange={e => set('highestQualification', e.target.value)} error={errors.highestQualification} />
              <Select label="Year of Graduation from Advanced FETP" required options={GRADUATION_YEARS} placeholder="Select year…" value={data.graduationYear} onChange={e => set('graduationYear', e.target.value)} error={errors.graduationYear} />
            </div>
            <Input label="Current Employer / Organization" value={data.currentEmployer} onChange={e => set('currentEmployer', e.target.value)} hint='If none, enter "N/A"' placeholder="e.g. WHO Uganda" />
            <Textarea label="Current Placement / Role Description" value={data.currentRole} onChange={e => set('currentRole', e.target.value)} hint="Briefly summarize key responsibilities. Skip if not currently employed." placeholder="Describe your current role…" />
            <Select label="Sector of Current Work" required options={SECTOR_OPTIONS} placeholder="Select sector…" value={data.sector} onChange={e => set('sector', e.target.value)} error={errors.sector} />
            {data.sector === 'Other' && (
              <Input label="Please specify sector" value={data.sectorOther} onChange={e => set('sectorOther', e.target.value)} placeholder="Describe your sector" />
            )}
            <Input label="Years of Professional Experience in Public Health" required type="number" min="0" max="60" value={data.yearsExperience} onChange={e => set('yearsExperience', e.target.value)} error={errors.yearsExperience} placeholder="e.g. 8" />
          </Card>
        )}

        {/* ── STEP 3: Availability ── */}
        {step === 3 && (
          <Card className={styles.card}>
            <SectionHeader letter="C" title="Availability for Deployment" subtitle="Help us understand when and how you can be deployed" />
            <RadioGroup label="Available for local deployments (within your country)?" required name="localDeploy" options={AVAILABILITY_OPTIONS} value={data.localDeployment} onChange={v => set('localDeployment', v)} />
            <RadioGroup label="Available for international deployments?" required name="intlDeploy" options={AVAILABILITY_OPTIONS} value={data.internationalDeployment} onChange={v => set('internationalDeployment', v)} />
            <CheckboxGroup label="Preferred Duration of Deployment (select all that apply)" items={DEPLOYMENT_DURATION} values={data.deploymentDuration} onChange={(key, val) => setNested('deploymentDuration', key, val)} />
            <RadioGroup label="Notice Period Required for Deployment" name="noticePeriod" options={NOTICE_PERIOD_OPTIONS} value={data.noticePeriod} onChange={v => set('noticePeriod', v)} />
            {data.noticePeriod === 'More than 1 month' && (
              <Input label="Please specify notice period" value={data.noticePeriodOther} onChange={e => set('noticePeriodOther', e.target.value)} placeholder="e.g. 6 weeks" />
            )}
            <Textarea label="Any restrictions on deployment?" value={data.deploymentRestrictions} onChange={e => set('deploymentRestrictions', e.target.value)} placeholder="e.g. specific regions, travel advisories, health conditions…" />
          </Card>
        )}

        {/* ── STEP 4: Skills ── */}
        {step === 4 && (
          <div className={styles.skillsPage}>
            <div className={styles.skillsIntro}>
              <SectionHeader letter="D" title="Key Skills & Expertise" subtitle="Rate your proficiency from 1 (Basic) to 5 (Expert)" />
              <div className={styles.ratingLegend}>
                {[1,2,3,4,5].map(n => (
                  <div key={n} className={styles.legendItem}>
                    <div className={styles.legendDot}>{n}</div>
                    <span>{['Basic','Limited','Proficient','Highly Proficient','Expert'][n-1]}</span>
                  </div>
                ))}
              </div>
            </div>
            <SkillSection
              title="1. Field Epidemiology Core Skills"
              skills={FIELD_EPI_SKILLS}
              section="fieldEpiSkills"
              data={data}
              setSkill={setSkill}
              otherSkillsValue={data.fieldEpiOtherSkills}
              onOtherSkillsChange={v => set('fieldEpiOtherSkills', v)}
            />
            {data.fieldEpiSkills.otherSoftware > 0 && (
              <div style={{ padding: '0 8px 16px' }}>
                <Input label="Other software name" value={data.otherSoftwareName} onChange={e => set('otherSoftwareName', e.target.value)} placeholder="e.g. Python, MATLAB" />
              </div>
            )}
            <SkillSection
              title="2. Project Management Skills"
              skills={PROJECT_MGMT_SKILLS}
              section="projectMgmtSkills"
              data={data}
              setSkill={setSkill}
              otherSkillsValue={data.projectMgmtOtherSkills}
              onOtherSkillsChange={v => set('projectMgmtOtherSkills', v)}
            />
            <SkillSection
              title="3. Monitoring & Evaluation (M&E) Skills"
              skills={ME_SKILLS}
              section="meSkills"
              data={data}
              setSkill={setSkill}
              otherSkillsValue={data.meOtherSkills}
              onOtherSkillsChange={v => set('meOtherSkills', v)}
            />
            <SkillSection
              title="4. Global Health Security (GHS) Skills"
              skills={GHS_SKILLS}
              section="ghsSkills"
              data={data}
              setSkill={setSkill}
              otherSkillsValue={data.ghsOtherSkills}
              onOtherSkillsChange={v => set('ghsOtherSkills', v)}
            />
            <SkillSection
              title="5. Incident Management & Outbreak Response"
              skills={INCIDENT_SKILLS}
              section="incidentSkills"
              data={data}
              setSkill={setSkill}
              otherSkillsValue={data.incidentOtherSkills}
              onOtherSkillsChange={v => set('incidentOtherSkills', v)}
            />
            <SkillSection
              title="6. Leadership & Training Skills"
              skills={LEADERSHIP_SKILLS}
              section="leadershipSkills"
              data={data}
              setSkill={setSkill}
              otherSkillsValue={data.leadershipOtherSkills}
              onOtherSkillsChange={v => set('leadershipOtherSkills', v)}
            />
          </div>
        )}

        {/* ── STEP 5: Experience ── */}
        {step === 5 && (
          <Card className={styles.card}>
            <SectionHeader letter="E" title="Specific Expertise & Experience" subtitle="Details about your specialized experience and exposure" />
            <Textarea label="Diseases/Pathogens You Have Specific Expertise & Experience With" value={data.diseasesExperience} onChange={e => set('diseasesExperience', e.target.value)} placeholder="e.g. Ebola, Cholera, COVID-19, Malaria, HIV/AIDS, TB, Polio…" rows={3} />

            <div className={styles.subsection}>
              <p className={styles.subsectionTitle}>Countries Where You Have Significant Work Experience (up to 5)</p>
              <div className={styles.regionsGrid}>
                {data.workRegions.map((r, i) => (
                  <Input key={i} placeholder={`Country ${i + 1}`} value={r} onChange={e => setRegion(i, e.target.value)} />
                ))}
              </div>
            </div>

            <div className={styles.subsection}>
              <p className={styles.subsectionTitle}>Continents of Work Experience (select all that apply)</p>
              <div className={styles.continentsGrid}>
                {CONTINENTS.map(continent => (
                  <label key={continent} className={styles.continentCheckLabel}>
                    <input
                      type="checkbox"
                      checked={(data.workContinents || []).includes(continent)}
                      onChange={() => toggleContinent(continent)}
                      className={styles.continentCheckInput}
                    />
                    <span className={styles.continentCheckText}>{continent}</span>
                  </label>
                ))}
              </div>
            </div>

            <RadioGroup label="Experience working in humanitarian emergencies or fragile settings?" name="humanitarian" options={['Yes', 'No']} value={data.humanitarianExperience} onChange={v => set('humanitarianExperience', v)} />
            {data.humanitarianExperience === 'Yes' && (
              <Textarea label="If yes, briefly describe the context" value={data.humanitarianContext} onChange={e => set('humanitarianContext', e.target.value)} placeholder="e.g. conflict zones, natural disaster response…" />
            )}
            <Textarea label="Specific Technical Certifications or Advanced Training" value={data.certifications} onChange={e => set('certifications', e.target.value)} placeholder="e.g. Biosafety Level 3, Incident Command System, Humanitarian Logistics…" />
            <RadioGroup label="Are you willing to work in challenging or high-risk environments?" name="highRisk" options={HIGH_RISK_OPTIONS} value={data.highRiskWillingness} onChange={v => set('highRiskWillingness', v)} />
            <Textarea label="Additional Skills or Areas of Expertise Not Covered Above" value={data.additionalSkills} onChange={e => set('additionalSkills', e.target.value)} placeholder="e.g. laboratory expertise, qualitative research, specific populations…" />
          </Card>
        )}

        {/* ── STEP 6: References + Consent ── */}
        {step === 6 && (
          <Card className={styles.card}>
            <SectionHeader letter="F" title="Professional References & Consent" subtitle="Optional, but highly recommended for rapid deployment consideration" />

            <RadioGroup
              label="Would you like to add professional references?"
              name="addReferences"
              options={['Yes', 'No']}
              value={data.addReferences}
              onChange={v => {
                set('addReferences', v);
                if (v === 'No') set('references', []);
              }}
            />

            {data.addReferences === 'Yes' && (
              <div className={styles.referencesSection}>
                {(data.references || []).map((ref, idx) => (
                  <div key={idx} className={styles.refBlock}>
                    <div className={styles.refBlockHeader}>
                      <p className={styles.refLabel}>Reference {idx + 1}</p>
                      <button
                        type="button"
                        className={styles.refRemoveBtn}
                        onClick={() => removeReference(idx)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className={styles.grid2}>
                      <Input
                        label="Name"
                        value={ref.name}
                        onChange={e => updateReference(idx, 'name', e.target.value)}
                        placeholder="Full name"
                      />
                      <Input
                        label="Title"
                        value={ref.title}
                        onChange={e => updateReference(idx, 'title', e.target.value)}
                        placeholder="e.g. Dr, Prof, Mr"
                      />
                    </div>
                    <Input
                      label="Organization"
                      value={ref.organization}
                      onChange={e => updateReference(idx, 'organization', e.target.value)}
                      placeholder="e.g. WHO, Ministry of Health"
                    />
                    <div className={styles.grid2}>
                      <Input
                        label="Email"
                        type="email"
                        value={ref.email}
                        onChange={e => updateReference(idx, 'email', e.target.value)}
                        placeholder="email@example.com"
                      />
                      <Input
                        label="Phone"
                        value={ref.phone}
                        onChange={e => updateReference(idx, 'phone', e.target.value)}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>
                ))}

                {(data.references || []).length < 3 && (
                  <button
                    type="button"
                    className={styles.addRefBtn}
                    onClick={addReference}
                  >
                    + Add Reference {(data.references || []).length + 1}
                    {(data.references || []).length === 0 ? '' : ` of 3`}
                  </button>
                )}
                {(data.references || []).length === 3 && (
                  <p className={styles.refMaxNote}>Maximum of 3 references reached.</p>
                )}
              </div>
            )}

            <div className={styles.consentBox}>
              <label className={styles.consentLabel}>
                <input type="checkbox" checked={data.consent} onChange={e => set('consent', e.target.checked)} className={styles.consentCheck} />
                <span>
                  I certify that the information provided in this questionnaire is accurate and complete to the best of my knowledge. I understand that this information will be used by Field Epidemiologists without Borders (FEwB) for internal rostering and deployment purposes. Any reference to this information will be in aggregate form, and all personal identifier information will be kept strictly confidential.
                </span>
              </label>
              {errors.consent && <p style={{ color: 'var(--error-text)', fontSize: '13px', marginTop: 8 }}>{errors.consent}</p>}
            </div>
          </Card>
        )}

        {/* ── Navigation ── */}
        <div className={styles.nav}>
          {step > 1 && <Button variant="secondary" onClick={prev}>← Back</Button>}
          <div style={{ flex: 1 }} />
          <Button variant="primary" onClick={next}>
            {step === TOTAL_STEPS ? 'Review Answers →' : 'Continue →'}
          </Button>
        </div>

      </main>
    </div>
  );
}

/* ── Sub-components ── */
function SkillSection({ title, skills, section, data, setSkill, otherSkillsValue, onOtherSkillsChange }) {
  return (
    <Card className={styles.skillCard}>
      <h3 className={styles.skillSectionTitle}>{title}</h3>
      <div className={styles.skillRows}>
        {skills.map(({ key, label }) => (
          <RatingRow
            key={key}
            label={label}
            value={data[section][key] || 0}
            onChange={val => setSkill(section, key, val)}
          />
        ))}
      </div>
      <div className={styles.otherSkillsRow}>
        <Textarea
          label="Other skills not listed above"
          value={otherSkillsValue}
          onChange={e => onOtherSkillsChange(e.target.value)}
          placeholder="Describe any additional skills in this area that are not listed above…"
          rows={2}
        />
      </div>
    </Card>
  );
}

function PreviewSection({ title, onEdit, children }) {
  return (
    <div className={styles.previewSection}>
      <div className={styles.previewSectionHead}>
        <h3 className={styles.previewSectionTitle}>{title}</h3>
        <button className={styles.editBtn} onClick={onEdit}>✏ Edit</button>
      </div>
      <div className={styles.previewSectionBody}>{children}</div>
    </div>
  );
}

function PreviewRow({ label, value }) {
  if (!value) return null;
  return (
    <div className={styles.previewRow}>
      <span className={styles.previewRowLabel}>{label}</span>
      <span className={styles.previewRowValue}>{value}</span>
    </div>
  );
}